import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider } from './context/BrandContext';
import { AccountProvider } from './context/AccountContext';

// Pages
import Login           from './pages/Login';
import Register        from './pages/Register';
import SetPassword     from './pages/SetPassword';
import BookingPage     from './pages/BookingPage';
import CancelBooking   from './pages/CancelBooking';
import Dashboard       from './pages/Dashboard';
import Leads           from './pages/Leads';
import Pipeline        from './pages/Pipeline';
import Team            from './pages/Team';
import Settings        from './pages/Settings';
import Email           from './pages/Email';
import Calendar        from './pages/Calendar';
import Platform        from './pages/Platform';
import AgencyDashboard from './pages/AgencyDashboard';
import Layout          from './components/Layout';
import Integrations    from './pages/Integrations';
import Onboarding      from './pages/Onboarding';
import Billing         from './pages/Billing';

// ── Spinner ───────────────────────────────────────────────────
const Spinner = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#f5f7fa' }}>
    <div style={{ textAlign:'center' }}>
      <div style={{ width:40, height:40, border:'3px solid #e5e7eb', borderTop:'3px solid #0099FF', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }} />
      <div style={{ fontSize:13, color:'#6b7280' }}>Loading...</div>
    </div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── Role rank helper ──────────────────────────────────────────
const ROLE_RANK = { account_user:0, account_admin:1, agency_user:1, agency_admin:2, platform_admin:3, owner:4 };
const hasMinRole = (role, min) => (ROLE_RANK[role]??0) >= (ROLE_RANK[min]??0);

// ── Protected route ───────────────────────────────────────────
const Protected = ({ children, minRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (minRole && !hasMinRole(user.role, minRole)) return <Navigate to="/" replace />;
  return children;
};

export default function App() {
  return (
    <BrandProvider>
      <AuthProvider>
        <AccountProvider>
          <BrowserRouter>
            <Routes>
              {/* ── Public routes (no layout/sidebar) ── */}
              <Route path="/login"         element={<Login />} />
              <Route path="/register"      element={<Register />} />
              <Route path="/onboarding"    element={<Onboarding />} />
              <Route path="/set-password"  element={<SetPassword />} />
              <Route path="/book/:slug"    element={<BookingPage />} />
              <Route path="/cancel/:token" element={<CancelBooking />} />

              {/* ── Protected routes (inside Layout with sidebar) ── */}
              <Route path="/" element={<Protected><Layout /></Protected>}>

                {/* Sub-account CRM */}
                <Route index             element={<Dashboard />} />
                <Route path="leads"      element={<Leads />} />
                <Route path="pipeline"   element={<Pipeline />} />
                <Route path="email"      element={<Email />} />
                <Route path="calendar"   element={<Calendar />} />
                <Route path="team"       element={<Protected minRole="account_admin"><Team /></Protected>} />
                <Route path="integrations" element={<Integrations />} />
                <Route path="settings"   element={<Settings />} />

                {/* Billing — inside CRM with sidebar */}
                <Route path="billing"    element={<Billing />} />

                {/* Agency */}
                <Route path="agency"          element={<Protected minRole="agency_admin"><AgencyDashboard /></Protected>} />
                <Route path="agency/accounts" element={<Protected minRole="agency_admin"><AgencyDashboard tab="accounts" /></Protected>} />
                <Route path="agency/branding" element={<Protected minRole="agency_admin"><AgencyDashboard tab="branding" /></Protected>} />
                <Route path="agency/domain"   element={<Protected minRole="agency_admin"><AgencyDashboard tab="domain" /></Protected>} />

                {/* Platform (owner + platform_admin only) */}
                <Route path="platform"          element={<Protected minRole="platform_admin"><Platform tab="overview" /></Protected>} />
                <Route path="platform/accounts" element={<Protected minRole="platform_admin"><Platform tab="accounts" /></Protected>} />
                <Route path="platform/plans"    element={<Protected minRole="platform_admin"><Platform tab="plans" /></Protected>} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AccountProvider>
      </AuthProvider>
    </BrandProvider>
  );
}
